// src/user/components/FormNode.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { useWorkflowCache } from '../contexts/WorkflowCacheContext';
import ActionButton from './ActionButton';
import WorkflowStepInstruction from './WorkflowStepInstruction';

function schemaIsValid(formData, schema, uiSchema) {
  const { errors } = validator.validateFormData(
    formData,
    schema,
    undefined,
    undefined,
    uiSchema || {}
  );
  return !errors || errors.length === 0;
}

/**
 * FormNode renders a JSON Schema form using RJSF with validation and draft management.
 * This replaces the legacy inline HTML form functionality.
 *
 * Props:
 *  - view: { id, graphic, heading, slug, actions }
 *  - accessKey: string access key for the task
 *  - taskId: string task ID
 *  - onNavigate: callback for navigation actions
 *  - onFormValidChange: callback when form validity changes
 */
export default function FormNode({
  view,
  accessKey,
  taskId,
  onNavigate,
  onFormValidChange,
  closeTask,
}) {
  const { graphic, heading, slug, actions } = view;
  const { getFormDefinition, getDraft, setDraft } = useWorkflowCache();

  const onFormValidChangeRef = useRef(onFormValidChange);
  onFormValidChangeRef.current = onFormValidChange;

  const draftTimerRef = useRef(null);
  const pendingDraftRef = useRef(null);

  const [formData, setFormData] = useState({});
  const [isValid, setIsValid] = useState(false);
  /** After user blurs a field, show RJSF inline errors and use live validation while editing. */
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const formDef = getFormDefinition({ accessKey, taskId, stepId: view.id, slug });

  const mergedUiSchema = useMemo(
    () => ({
      ...(formDef?.uiSchema || {}),
      'ui:submitButtonOptions': {
        ...(formDef?.uiSchema?.['ui:submitButtonOptions'] || {}),
        norender: true,
      },
    }),
    [formDef]
  );

  useEffect(() => {
    if (!formDef) return;

    setShowFieldErrors(false);
    const draft = getDraft({ accessKey, taskId, stepId: view.id, slug });
    const initialData = draft || formDef.defaults || {};
    setFormData(initialData);

    const valid = schemaIsValid(initialData, formDef.schema, mergedUiSchema);
    setIsValid(valid);
    onFormValidChangeRef.current?.(valid);
  }, [formDef, accessKey, taskId, view.id, slug, getDraft, mergedUiSchema]);

  useEffect(() => {
    return () => {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      if (pendingDraftRef.current) {
        setDraft({
          accessKey,
          taskId,
          stepId: view.id,
          slug,
          answers: pendingDraftRef.current,
        });
        pendingDraftRef.current = null;
      }
    };
  }, [accessKey, taskId, view.id, slug, setDraft]);

  const handleFormChange = useCallback(
    (evt) => {
      const newFormData = evt.formData;
      setFormData(newFormData);

      const valid = schemaIsValid(newFormData, formDef.schema, mergedUiSchema);
      setIsValid(valid);

      pendingDraftRef.current = newFormData;
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
      }
      draftTimerRef.current = setTimeout(() => {
        setDraft({ accessKey, taskId, stepId: view.id, slug, answers: newFormData });
        pendingDraftRef.current = null;
        draftTimerRef.current = null;
      }, 500);

      onFormValidChangeRef.current?.(valid);
    },
    [formDef, mergedUiSchema, accessKey, taskId, view.id, slug, setDraft]
  );

  const handleFormBlur = useCallback(() => {
    setShowFieldErrors(true);
  }, []);

  const handleFormSubmit = () => {
    // Form submission is handled by navigation actions, not direct submission
  };

  if (!formDef) {
    return (
      <div className="container mt-3">
        <div className="alert alert-warning">
          <h4>Form Not Found</h4>
          <p>Unable to load form definition for "{slug}". Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-3" id={`node-content-${view.id}`}>
      <WorkflowStepInstruction graphic={graphic} graphicOnly />
      <h2>{heading}</h2>

      <div className="rjsf-form">
        <Form
          schema={formDef.schema}
          uiSchema={mergedUiSchema}
          formData={formData}
          onChange={handleFormChange}
          onBlur={handleFormBlur}
          onSubmit={handleFormSubmit}
          validator={validator}
          showErrorList={false}
          noHtml5Validate={true}
          liveValidate={showFieldErrors}
        />
      </div>

      {(actions?.length > 0 || closeTask) && (
        <div className="row mt-4">
          {actions &&
            actions.map((action, index) => (
              <div key={index} className="col-12 mb-2">
                <ActionButton
                  action={action}
                  fromStepId={view.id}
                  actionIndex={index}
                  taskId={taskId}
                  accessKeyToken={accessKey}
                  onNavigate={(targetViewId) => onNavigate(targetViewId)}
                  disabled={!isValid}
                />
              </div>
            ))}
          {closeTask && (
            <div className="col-12 mb-2">
              <ActionButton
                isCloseTask
                taskId={closeTask.taskId}
                accessKeyToken={closeTask.accessKeyToken}
                onTaskClosed={closeTask.onTaskClosed}
                afterClosePath={closeTask.afterClosePath}
                disabled={closeTask.disabled || !isValid}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
